import React from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { PrimaryButton, SecondaryButton } from '../../styled/button'

const LandingContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
`

const Hero = styled.section`
  text-align: center;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.primary}15 0%, ${({ theme }) => theme.secondary}15 100%);

  h1 {
    font-size: 3rem;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.bodyText};

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  .tagline {
    font-size: 1.5rem;
    color: ${({ theme }) => theme.secondaryText};
    margin-bottom: 2rem;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }

  .cta-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 2rem;

    button, a {
      min-width: 180px;
      text-decoration: none;
    }
  }
`

const FeaturesSection = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;

  h2 {
    text-align: center;
    font-size: 2.5rem;
    margin-bottom: 3rem;
    color: ${({ theme }) => theme.bodyText};
  }
`

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`

const FeatureCard = styled.div`
  background: ${({ theme }) => theme.secondaryBackground};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  padding: 2rem;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
  }

  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  h3 {
    color: ${({ theme }) => theme.primary};
    margin-bottom: 1rem;
    font-size: 1.5rem;
  }

  p {
    color: ${({ theme }) => theme.secondaryText};
    line-height: 1.6;
  }

  ul {
    list-style: none;
    padding: 0;
    margin-top: 1rem;

    li {
      padding: 0.5rem 0;
      color: ${({ theme }) => theme.bodyText};

      &:before {
        content: '✓ ';
        color: ${({ theme }) => theme.primary};
        font-weight: bold;
        margin-right: 0.5rem;
      }
    }
  }
`

const UseCaseSection = styled.section`
  background: ${({ theme }) => theme.secondaryBackground};
  padding: 4rem 2rem;

  h2 {
    text-align: center;
    font-size: 2.5rem;
    margin-bottom: 3rem;
    color: ${({ theme }) => theme.bodyText};
  }
`

const UseCaseGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
`

const UseCaseCard = styled.div`
  background: ${({ theme }) => theme.background};
  border-radius: 8px;
  padding: 2rem;
  text-align: center;

  .emoji {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  h3 {
    color: ${({ theme }) => theme.primary};
    margin-bottom: 1rem;
  }

  p {
    color: ${({ theme }) => theme.secondaryText};
    line-height: 1.6;
  }
`

const CollaborationSection = styled.section`
  padding: 4rem 2rem;
  max-width: 1000px;
  margin: 0 auto;
  text-align: center;

  h2 {
    font-size: 2.5rem;
    margin-bottom: 2rem;
    color: ${({ theme }) => theme.bodyText};
  }

  .highlight-box {
    background: ${({ theme }) => theme.secondaryBackground};
    border-left: 4px solid ${({ theme }) => theme.primary};
    padding: 2rem;
    margin: 2rem 0;
    text-align: left;

    h3 {
      color: ${({ theme }) => theme.primary};
      margin-bottom: 1rem;
    }

    p {
      color: ${({ theme }) => theme.secondaryText};
      line-height: 1.8;
      margin-bottom: 1rem;
    }
  }
`

const TechFeaturesSection = styled.section`
  background: ${({ theme }) => theme.secondaryBackground};
  padding: 4rem 2rem;

  h2 {
    text-align: center;
    font-size: 2.5rem;
    margin-bottom: 3rem;
    color: ${({ theme }) => theme.bodyText};
  }
`

const TechGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`

const TechItem = styled.div`
  background: ${({ theme }) => theme.background};
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.borderColor};

  h4 {
    color: ${({ theme }) => theme.primary};
    margin-bottom: 0.5rem;
  }

  p {
    color: ${({ theme }) => theme.secondaryText};
    font-size: 0.95rem;
  }
`

const FinalCTA = styled.section`
  padding: 4rem 2rem;
  text-align: center;
  background: linear-gradient(135deg, ${({ theme }) => theme.primary}10 0%, ${({ theme }) => theme.secondary}10 100%);

  h2 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.bodyText};
  }

  p {
    font-size: 1.2rem;
    color: ${({ theme }) => theme.secondaryText};
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }

  .cta-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;

    button, a {
      min-width: 200px;
      text-decoration: none;
    }
  }
`

const Marketing = () => {
  return (
    <LandingContainer>
      <Hero>
        <h1>🎸 Your Band's Digital Songbook</h1>
        <p className='tagline'>
          Stop fumbling with paper tabs and out-of-sync versions. Tabify keeps your songs, setlists, and band perfectly organized.
        </p>
        <div className='cta-buttons'>
          <Link to='/register'>
            <PrimaryButton>Start Free</PrimaryButton>
          </Link>
          <Link to='/pricing'>
            <SecondaryButton>View Pricing</SecondaryButton>
          </Link>
        </div>
      </Hero>

      <FeaturesSection>
        <h2>Everything Your Band Needs</h2>
        <FeatureGrid>
          <FeatureCard>
            <div className='icon'>📝</div>
            <h3>Smart Song Management</h3>
            <p>Create and organize your entire song library with powerful tools designed for musicians.</p>
            <ul>
              <li>Chord sheets with automatic formatting</li>
              <li>Lyrics and tabs in one place</li>
              <li>Transpose to any key instantly</li>
              <li>Auto-detect song keys</li>
              <li>Tag and categorize songs</li>
              <li>Link to Spotify tracks</li>
            </ul>
          </FeatureCard>

          <FeatureCard>
            <div className='icon'>🎵</div>
            <h3>Setlist Builder</h3>
            <p>Build professional setlists for rehearsals and performances with drag-and-drop simplicity.</p>
            <ul>
              <li>Drag-and-drop song ordering</li>
              <li>Calculate total set duration</li>
              <li>Share setlists with your band</li>
              <li>Export for printing</li>
              <li>Link setlists to shows</li>
              <li>Quick navigation during performance</li>
            </ul>
          </FeatureCard>

          <FeatureCard>
            <div className='icon'>🎤</div>
            <h3>Synchronized Rehearsal Mode</h3>
            <p>One person controls the setlist — everyone's screen updates in real-time.</p>
            <ul>
              <li>Live sync across all devices</li>
              <li>Leader controls song navigation</li>
              <li>Everyone sees the same song</li>
              <li>Track rehearsal history</li>
              <li>Mark songs as practiced</li>
              <li>No more "which song are we on?"</li>
            </ul>
          </FeatureCard>

          <FeatureCard>
            <div className='icon'>👥</div>
            <h3>Band Collaboration</h3>
            <p>Share songs and setlists with your band members and manage permissions easily.</p>
            <ul>
              <li>Create groups for your bands</li>
              <li>Share with individuals or groups</li>
              <li>Granular view/edit permissions</li>
              <li>Real-time notifications</li>
              <li>Version history and backups</li>
              <li>Multiple song versions</li>
            </ul>
          </FeatureCard>

          <FeatureCard>
            <div className='icon'>🎼</div>
            <h3>Built-in Metronome</h3>
            <p>Practice with a professional metronome featuring multiple sound profiles.</p>
            <ul>
              <li>Adjustable tempo and time signature</li>
              <li>Visual beat indicator</li>
              <li>Works alongside song display</li>
            </ul>
          </FeatureCard>

          <FeatureCard>
            <div className='icon'>📱</div>
            <h3>Works Everywhere</h3>
            <p>Access your songs on any device, online or offline.</p>
            <ul>
              <li>Web app works on any browser</li>
              <li>Native mobile apps (iOS & Android)</li>
              <li>Offline mode for gigs without WiFi</li>
              <li>Dark mode for stage lighting</li>
              <li>Responsive design</li>
              <li>Sync across all devices</li>
            </ul>
          </FeatureCard>
        </FeatureGrid>
      </FeaturesSection>

      <UseCaseSection>
        <h2>Perfect For Every Musician</h2>
        <UseCaseGrid>
          <UseCaseCard>
            <div className='emoji'>🎸</div>
            <h3>Solo Musicians</h3>
            <p>Build your personal songbook, practice with the metronome, and transpose songs to your preferred keys.</p>
          </UseCaseCard>

          <UseCaseCard>
            <div className='emoji'>🎹</div>
            <h3>Cover Bands</h3>
            <p>Manage hundreds of songs, create setlists for different venues, and keep everyone on the same page during rehearsals.</p>
          </UseCaseCard>

          <UseCaseCard>
            <div className='emoji'>🎤</div>
            <h3>Original Artists</h3>
            <p>Write and arrange your originals, share with bandmates, track different versions, and prepare for studio sessions.</p>
          </UseCaseCard>

          <UseCaseCard>
            <div className='emoji'>🎓</div>
            <h3>Music Teachers</h3>
            <p>Organize lesson materials, share songs with students, and track practice progress across multiple students.</p>
          </UseCaseCard>
        </UseCaseGrid>
      </UseCaseSection>

      <CollaborationSection>
        <h2>Stop the "Which Version?" Chaos</h2>
        <div className='highlight-box'>
          <h3>The Problem</h3>
          <p>
            You've been there: halfway through rehearsal, someone asks "wait, are we doing the version with the bridge or without?"
            Half the band has the old chart, someone's looking at their phone with different chords, and five minutes of rehearsal time vanishes.
          </p>
        </div>
        <div className='highlight-box'>
          <h3>The Tabify Solution</h3>
          <p>
            With Tabify, everyone sees the same version, always. Make an edit? It syncs instantly to your whole band.
            Start rehearsal mode? Everyone's screen follows the leader. No more confusion, no more wasted time.
          </p>
          <p>
            Plus, with version history, you can always go back to that arrangement you tried last month.
          </p>
        </div>
      </CollaborationSection>

      <TechFeaturesSection>
        <h2>Powerful Features for Serious Musicians</h2>
        <TechGrid>
          <TechItem>
            <h4>🎯 Chord Transposition</h4>
            <p>Instantly transpose any song to match your vocalist's range or your preferred key.</p>
          </TechItem>

          <TechItem>
            <h4>🔍 Auto Key Detection</h4>
            <p>Let Tabify analyze your chords and suggest the song's key automatically.</p>
          </TechItem>

          <TechItem>
            <h4>📊 Show Management</h4>
            <p>Schedule gigs, link setlists, and keep track of your performance calendar.</p>
          </TechItem>

          <TechItem>
            <h4>🏷️ Smart Tagging</h4>
            <p>Organize songs by genre, mood, difficulty, or any custom tags you create.</p>
          </TechItem>

          <TechItem>
            <h4>🔗 Spotify Integration</h4>
            <p>Link songs to Spotify tracks for quick reference and learning.</p>
          </TechItem>

          <TechItem>
            <h4>📧 Email Invitations</h4>
            <p>Invite bandmates via email, even if they don't have an account yet.</p>
          </TechItem>

          <TechItem>
            <h4>🔔 Smart Notifications</h4>
            <p>Get notified when songs are edited, added, or when rehearsals are scheduled.</p>
          </TechItem>

          <TechItem>
            <h4>💾 Version Control</h4>
            <p>Save multiple versions of songs and switch between arrangements easily.</p>
          </TechItem>

          <TechItem>
            <h4>📤 Export & Print</h4>
            <p>Export setlists and songs for printing or sharing outside the app.</p>
          </TechItem>

          <TechItem>
            <h4>🌙 Dark Mode</h4>
            <p>Easy on the eyes during late-night rehearsals and dark stages.</p>
          </TechItem>

          <TechItem>
            <h4>⚡ Lightning Fast</h4>
            <p>Built for performance with instant loading and smooth navigation.</p>
          </TechItem>

          <TechItem>
            <h4>🔒 Secure & Private</h4>
            <p>Your songs are yours. Control exactly who can see and edit your content.</p>
          </TechItem>
        </TechGrid>
      </TechFeaturesSection>

      <FinalCTA>
        <h2>Ready to Get Your Band in Sync?</h2>
        <p>
          Join thousands of musicians who've ditched the paper chaos and upgraded to Tabify.
          Start free, upgrade when you're ready.
        </p>
        <div className='cta-buttons'>
          <Link to='/register'>
            <PrimaryButton>Create Free Account</PrimaryButton>
          </Link>
          <Link to='/login'>
            <SecondaryButton>Sign In</SecondaryButton>
          </Link>
        </div>
      </FinalCTA>
    </LandingContainer>
  )
}

export default Marketing
